import { useEffect, useRef } from "react";
import { useApp } from "../store.jsx";
import { useRole } from "../hooks/useRole";
import { listNotifications } from "../lib/api";
import { showBrowserNotification } from "../lib/browserNotify";

const POLL_MS = 45000;

// Mounted once inside the authenticated app shell. Polls the notification feed,
// pushes results into the store (drives the bell + drawer), and fires a
// foreground browser notification for any newly-arrived unread item.
export default function NotificationsPoller() {
  const { setNotifications, browserNotifyEnabled, goPage, setNotifOpen } = useApp();
  const role = useRole();
  // null = not seeded yet; otherwise the Set of unread ids we've already
  // surfaced (so we don't re-pop the OS toast or blast the backlog on load).
  const seenRef = useRef(null);

  useEffect(() => {
    if (!role || role === "admin") return;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await listNotifications(30);
        if (cancelled) return;
        const items = data?.items || [];
        setNotifications(items);

        const unreadIds = items.filter((n) => !n.readAt).map((n) => n.id);
        if (seenRef.current === null) {
          // First successful poll — seed without notifying (avoid a backlog blast).
          seenRef.current = new Set(unreadIds);
          return;
        }
        const fresh = items.filter(
          (n) => !n.readAt && !seenRef.current.has(n.id),
        );
        for (const n of fresh) {
          seenRef.current.add(n.id);
          if (browserNotifyEnabled) {
            showBrowserNotification({
              title: n.title || "New notification",
              body: n.body || "",
              tag: n.id,
              onClick: () => {
                setNotifOpen(true);
                if (n.targetType === "report") goPage("reports");
              },
            });
          }
        }
        // Keep the seen-set from growing unbounded: retain only still-unread ids.
        const unreadSet = new Set(unreadIds);
        seenRef.current = new Set(
          [...seenRef.current].filter((id) => unreadSet.has(id)),
        );
      } catch {
        /* transient poll failure — try again next tick */
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [role, browserNotifyEnabled, setNotifications, goPage, setNotifOpen]);

  return null;
}
